import { Field, Form, Formik } from "formik";
import { capitalize } from "lodash";
import { MouseEventHandler, useRef } from "react";

import { CaseDocumentAdmin as OpenApiDocument } from "pwapi";

import { CaseDocumentAdmin } from "custom-models";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import { AdminFieldCase } from "../edit/fields";
import { CaseOption } from "../edit/ResourceOptions";

type DocumentsFormProps = {
  document: CaseDocumentAdmin;
  adminCase?: CaseAdminDetail;
  onSubmit: Function;
  onDelete?: Function;
  onClose: MouseEventHandler<HTMLButtonElement>;
  isUploading?: boolean;
  hasFlatCase?: boolean;
};

const DocumentsForm = ({
  document,
  onSubmit,
  onDelete,
  onClose,
  isUploading,
  hasFlatCase,
}: DocumentsFormProps) => {
  const getFileExtension = (filename: string): string | undefined => {
    const regexMatch = filename.match(/\.[^/.]+$/);
    return regexMatch?.length ? regexMatch[0] : undefined;
  };

  const fileExtension = useRef<string | undefined>(
    document.name ? getFileExtension(document.name) : undefined
  );

  return (
    <Formik
      initialValues={document}
      enableReinitialize
      onSubmit={(values) => onSubmit(values)}
    >
      {({ resetForm, dirty }) => (
        <Form className="AdminPanelForm">
          <div className="panelContainer">
            {isUploading && (
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Upload
                </legend>
                <Field name="file">
                  {({ field, form }: any) => {
                    return (
                      <input
                        type="file"
                        accept="application/pdf"
                        data-testid="uploadDocumentFile"
                        {...field}
                        value={field.value ? undefined : ""}
                        onChange={(newFile: any) => {
                          form.setValues({
                            ...form.values,
                            file: newFile.target.files[0],
                            name: newFile.target.files[0].name,
                          });
                        }}
                      />
                    );
                  }}
                </Field>
              </fieldset>
            )}
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Case Details
              </legend>
              {hasFlatCase && document.case ? (
                <CaseOption object={document.case} />
              ) : (
                <Field name="case">
                  {({ field, form }: any) => (
                    <AdminFieldCase
                      {...field}
                      onChange={(newValue: any) => {
                        form.setValues({
                          ...form.values,
                          case: newValue.object,
                        });
                      }}
                    />
                  )}
                </Field>
              )}
            </fieldset>
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Documents Details
              </legend>
              <div className="file__container">
                <Field name="name">
                  {({ field }: any) => (
                    <TextInput label="Name" id="name" {...field} />
                  )}
                </Field>
              </div>
              <div className="panelContainer__inputGroup">
                {isUploading ? (
                  <div className="file__formatContainer">
                    <label>Status</label>
                    <span>{OpenApiDocument.status.PENDING}</span>
                  </div>
                ) : (
                  <Field name="status">
                    {({ field }: any) => (
                      <SelectInput
                        label="Status"
                        id="status"
                        options={Object.values(OpenApiDocument.status).map(
                          (p) => ({
                            label: capitalize(p),
                            value: p,
                          })
                        )}
                        {...field}
                      />
                    )}
                  </Field>
                )}
                <Field name="category">
                  {({ field }: any) => (
                    <SelectInput
                      label="Category"
                      id="category"
                      options={Object.values(OpenApiDocument.category).map(
                        (p) => ({
                          label: p.replace("-", " "),
                          value: p,
                        })
                      )}
                      {...field}
                    />
                  )}
                </Field>
              </div>
            </fieldset>
          </div>
          <footer>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton onClick={onClose}>
                Cancel
              </PathologyWatchButton>
              {onDelete &&
                document &&
                document.category !== OpenApiDocument.category.CASE_REPORT && (
                  <PathologyWatchButton
                    color="red"
                    onClick={() => {
                      onDelete(document);
                    }}
                  >
                    Delete
                  </PathologyWatchButton>
                )}
            </div>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton
                onClick={() => {
                  resetForm();
                  if (isUploading) {
                    fileExtension.current = undefined;
                  }
                }}
                disabled={!dirty}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                color="green"
                disabled={!dirty}
              >
                Save
              </PathologyWatchButton>
            </div>
          </footer>
        </Form>
      )}
    </Formik>
  );
};

export default DocumentsForm;
